/**
 * subscription-widget.js
 * Subscription Widget Controller v3.8
 * Fixed: Slow connection widget load, double observer loop, retryCount reset
 */

(function() {
  'use strict';
  
  const CONFIG = {
    RETRY_DELAY: 1500,   // ✅ FIX 1: Was 1000 — increased for slow USA CDN
    RETRY_MAX: 8,        // ✅ FIX 1: Was 5 — now 12 seconds total wait
    LOCK_INTERVAL: 500,
    CART_LOCK_DEBOUNCE: 300
  };
  
  let state = { qty: 1, plan: null, locked: false };
  let retryCount = 0;
  let cartLockTimeout = null;
  
  // ✅ SPLIT INTO TWO FUNCTIONS
  const initProductWidget = () => {
    const widget = document.querySelector('.sub-widget');
    if (!widget) return;
    
    const appstle = document.querySelector('#appstle_selling_plan0');
    if (!appstle) {
      if (retryCount < CONFIG.RETRY_MAX) {
        retryCount++;
        return setTimeout(initProductWidget, CONFIG.RETRY_DELAY);
      }
      return;
    }
    
    selectDefault(widget);
    attachListeners(widget);
    lockQuantity();
  };
  
  // ✅ NEW: Always run cart watcher globally
  const initGlobalCartLocking = () => {
    setupCartWatcher();
  };
  
  const attachListeners = widget => {
    widget.addEventListener('change', e => {
      if (e.target.matches('.sub-widget__radio')) {
        syncPlan(e.target);
      }
    });
  };
  
  const selectDefault = widget => {
    const radio = widget.querySelector('[data-qty="3"][data-plan]') || 
                  widget.querySelector('[data-plan]');
    
    if (radio) {
      radio.checked = true;
      syncPlan(radio);
    }
  };
  
  const syncPlan = radio => {
    if (!radio?.dataset) return;
    
    const qty = parseInt(radio.dataset.qty) || 1;
    const plan = radio.dataset.plan || '';
    const locked = !!plan;
    
    state = { qty, plan, locked };
    
    if (!updateAppstle()) return;
    
    updateQuantity();
    updateAddToCartPrice();
    notifyStickyATC();
    toggleAddonVisibility();
  };
  
  const updateAddToCartPrice = () => {
    const widget = document.querySelector('.sub-widget');
    if (!widget) return;
    
    const submitButton = document.querySelector('.product-form__submit span');
    if (!submitButton) return;
    
    const checkedRadio = widget.querySelector('.sub-widget__radio:checked');
    if (!checkedRadio) return;
    
    const priceContainer = checkedRadio.closest('li, .sub-widget__item');
    const priceElement = priceContainer?.querySelector('.sub-widget__price');
    
    if (priceElement) {
      const priceText = priceElement.textContent.trim();
      const priceMatch = priceText.match(/\$[\d,]+\.?\d*/);
      
      if (priceMatch) {
        submitButton.textContent = `ADD TO CART ${priceMatch[0]}`;
        return;
      }
    }
    
    submitButton.textContent = 'ADD TO CART';
  };
  
  const updateAppstle = () => {
    const dropdown = document.querySelector('#appstle_selling_plan0');
    if (!dropdown) return false;
    
    if (state.plan) {
      dropdown.value = state.plan;
      dropdown.dispatchEvent(new Event('change', { bubbles: true }));
      
      const subRadio = document.querySelector(`input[name="selling_plan"][value="${state.plan}"]`);
      if (subRadio) subRadio.click();
    } else {
      const oneRadio = document.querySelector('input[name="selling_plan"][value=""]');
      if (oneRadio) oneRadio.click();
    }
    
    return true;
  };
  
  const updateQuantity = () => {
    const inputs = document.querySelectorAll('input[name="quantity"]');
    const buttons = document.querySelectorAll('.quantity__button');
    
    if (!inputs.length) return;
    
    inputs.forEach(input => {
      input.value = state.qty;
      input.readOnly = state.locked;
      input.min = state.locked ? state.qty : 1;
      input.max = state.locked ? state.qty : 999;
    });
    
    buttons.forEach(btn => {
      btn.disabled = state.locked;
      btn.style.opacity = state.locked ? '0.4' : '1';
      btn.style.pointerEvents = state.locked ? 'none' : 'auto';
    });
  };
  
  const lockQuantity = () => {
    setInterval(() => {
      if (!state.locked) return;
      
      document.querySelectorAll('input[name="quantity"]').forEach(input => {
        if (parseInt(input.value) !== state.qty) {
          input.value = state.qty;
        }
      });
    }, CONFIG.LOCK_INTERVAL);
  };
  
  const notifyStickyATC = () => {
    try {
      document.dispatchEvent(new CustomEvent('subscription:changed', {
        detail: { 
          quantity: state.qty, 
          isSubscription: state.locked,
          plan: state.plan 
        }
      }));
    } catch (e) {
      console.error('[SubWidget] Notify error:', e);
    }
  };
  
  const toggleAddonVisibility = () => {
    const addonBlock = document.querySelector('.subscription-addon-wrapper');
    if (!addonBlock) return;
    
    addonBlock.style.display = (state.locked && state.plan) ? 'block' : 'none';
  };
  
  // ✅ GLOBAL: Works on all pages
  const applyCartLocks = () => {
    clearTimeout(cartLockTimeout);
    cartLockTimeout = setTimeout(() => {
      fetch('/cart.js')
        .then(r => r.ok ? r.json() : Promise.reject())
        .then(cart => {
          if (!cart?.items) return;

          // ✅ Remove free gifts whose parent product is no longer in cart
          const mainVariantIds = new Set(
            cart.items
              .filter(item => item.properties?._subscription_addon !== 'true')
              .map(item => String(item.variant_id))
          );

          const orphanedKeys = cart.items
            .filter(item =>
              item.properties?._subscription_addon === 'true' &&
              item.properties?._linked_to &&
              !mainVariantIds.has(String(item.properties._linked_to))
            )
            .map(item => item.key);

          if (orphanedKeys.length > 0) {
            const updates = {};
            orphanedKeys.forEach(key => { updates[key] = 0; });
            fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates })
            })
            .then(r => r.json())
            .then(() => {
              const cartDrawer = document.querySelector('cart-drawer');
              if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
                const sectionIds = (cartDrawer.getSectionsToRender?.() || [])
                  .map(s => s.id).join(',');
                if (sectionIds) {
                  fetch(`/?sections=${sectionIds}`)
                    .then(r => r.json())
                    .then(data => cartDrawer.renderContents({ sections: data }))
                    .catch(() => {});
                }
              }
            })
            .catch(() => {});
            return; // Stop here; re-render will trigger a fresh applyCartLocks
          }

          const addonsToFix = [];
          
          cart.items.forEach((item, i) => {
            const input = document.querySelector(`#Drawer-quantity-${i + 1}`) || 
                         document.querySelector(`#Quantity-${i + 1}`);
            
            if (!input) return;
            
            const isSubscription = item.selling_plan_allocation !== null && 
                                  item.selling_plan_allocation !== undefined;
            const isAddon = item.properties?._subscription_addon === 'true';
            
            if (isAddon && item.quantity > 1) {
              addonsToFix.push({ line: i + 1, id: item.id });
            }
            
            if (isAddon && parseInt(input.value) !== 1) {
              input.value = 1;
            } else if (parseInt(input.value) !== item.quantity) {
              input.value = item.quantity;
            }
            
            const shouldLock = isSubscription || isAddon;
            
            input.readOnly = shouldLock;
            input.style.pointerEvents = shouldLock ? 'none' : 'auto';
            
            const lineItem = input.closest('tr, .cart-item, .cart__item');
            const buttons = lineItem?.querySelectorAll('.quantity__button, button[name="minus"], button[name="plus"]');
            
            buttons?.forEach(btn => {
              btn.disabled = shouldLock;
              btn.style.opacity = shouldLock ? '0.4' : '1';
              btn.style.pointerEvents = shouldLock ? 'none' : 'auto';
            });
          });
          
          if (addonsToFix.length > 0) {
            addonsToFix.forEach(addon => {
              fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ line: addon.line, quantity: 1 })
              }).catch(() => {});
            });
          }
        })
        .catch(() => {});
    }, CONFIG.CART_LOCK_DEBOUNCE);
  };
  
  // ✅ GLOBAL: Runs on all pages
  const setupCartWatcher = () => {
    applyCartLocks();
    
    if (typeof subscribe === 'function') {
      subscribe('cartUpdate', () => {
        setTimeout(applyCartLocks, 50);
      });
    }
    
    // ✅ FIX: Refresh cart when back from checkout
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        setTimeout(applyCartLocks, 200);
      }
    });

    // ✅ FIX 4: Removed body observer — it overlapped with drawer observer
    // and caused applyCartLocks to fire in an infinite loop on USA slow connections.
    // The drawer observer below is sufficient for all cart drawer updates.

    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      let observerLocked = false; // ✅ FIX 4: Re-entrancy guard stops loop
      const drawerObserver = new MutationObserver(() => {
        if (observerLocked) return;
        observerLocked = true;
        setTimeout(() => {
          applyCartLocks();
          observerLocked = false;
        }, CONFIG.CART_LOCK_DEBOUNCE);
      });
      
      drawerObserver.observe(drawer, {
        childList: true,
        subtree: true
      });
    }
  };
  
  // ✅ INITIALIZE BOTH ON PAGE LOAD
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initProductWidget();        // Product page features
      initGlobalCartLocking();    // Cart locking everywhere
    });
  } else {
    initProductWidget();
    initGlobalCartLocking();
  }

  // ✅ FIX 5: Reset retryCount so lightbox re-call works fresh
  window.initProductWidget = () => {
    retryCount = 0;
    initProductWidget();
  };
  
})();
